/**
 * The privacy policy, as a page of the site.
 *
 * The text is carried over from the standalone privacy.html this replaces (membership pricing corrected 2026-09-30), which lived
 * outside the portal in a purple theme of its own. It is now served at `/privacy`. The old `/privacy.html` address, which may already be given out
 * (the Play Console listing, in particular), is redirected to it by htaccess.template.
 *
 * It is the policy for the Android app. The wording is the developer's. It had a plain-language
 * copy pass on 2026-10-01 that kept every claim as it was. Change it deliberately, and move the
 * "Last updated" date when you do.
 */

const SECTIONS = [
  ['device', 'Information stored only on your device'],
  ['server', 'Information sent to our server'],
  ['permissions', 'Device permissions and how they are used'],
  ['third-parties', 'Third-party services'],
  ['use', 'How we use your information'],
  ['retention', 'Data retention'],
  ['choices', 'Your choices and rights'],
  ['security', 'Security'],
  ['children', 'Children'],
  ['changes', 'Changes to this policy'],
  ['contact', 'Contact us'],
] as const

export function PrivacyPage() {
  return (
    <main className="wrap main">
      <article className="card doc col-main">
        <h1 className="doc-title">Privacy Policy</h1>
        <p className="doc-sub">
          Covellah keeps your data on your phone. No ads, no third-party analytics, and we never
          sell your data.
        </p>
        <div className="doc-meta">
          <div>
            <b>Effective</b> 31 August 2026
          </div>
          <div>
            <b>Last updated</b> 1 October 2026
          </div>
          <div>
            <b>Applies to</b> Covellah for Android
          </div>
        </div>

        <div className="doc-body">
          <p>
            This policy explains how the <strong>Covellah</strong> app ("Covellah," "the app,"
            "we," "us") handles your information. Covellah is a Torah-lifestyle companion for
            women, published by the maker of Torahly ("the developer"). By using Covellah you agree
            to what is described here.
          </p>

          <div className="doc-tldr">
            <div className="label">The short version</div>
            <p>
              Almost everything you track in Covellah stays on your device: your cycle, journal,
              prayers, health, and habits. A few features send data to our server because they
              can't work otherwise: your account, community boards, recipe sharing, and optional
              encrypted sync and account backup. Each is described below. We don't sell your data, show ads, or use
              third-party advertising or analytics trackers.
            </p>
          </div>

          <h2 id="device">1. Information stored only on your device</h2>
          <p>
            This stays on your phone and is never sent to us, unless you turn on an optional
            feature that needs it, such as encrypted sync. Uninstalling the app removes it:
          </p>
          <ul>
            <li>
              <strong>Cycle &amp; period data</strong>: start and end dates, flow, symptoms, notes,
              predictions, and the optional Leviticus 15 and 12 counting (off by default).
            </li>
            <li>
              <strong>Journal &amp; private prayers</strong>: your personal gratitude and prayer
              entries.
            </li>
            <li>
              <strong>Health tracking</strong>: food and water logs, fasting sessions, sleep and
              night watches, step counts, and workout history.
            </li>
            <li>
              <strong>Habits &amp; home rhythms</strong>: habit streaks, homemaking checklists, and
              reminders.
            </li>
            <li>
              <strong>Reading progress</strong>: your place in reading plans.
            </li>
            <li>
              <strong>Shopping list and app preferences</strong>, including the town you name so
              the app can work out your local sunset. Covellah does <strong>not</strong> request or
              use your device's GPS location.
            </li>
          </ul>

          <h3>Automatic backup (optional)</h3>
          <p>
            Covellah can back up your data to a folder you choose on your phone. Backup files are
            encrypted with a PIN or passphrase you set. Covellah doesn't keep your PIN, and the
            developer can't open your backups. If you forget it, they can't be restored.
            Uninstalling the app doesn't delete these files. They stay in your folder until you
            delete them. If that folder syncs to a cloud service such as Google Drive, that service
            handles the encrypted files under its own privacy policy.
          </p>

          <h2 id="server">2. Information sent to our server</h2>
          <p>
            Covellah connects to a server run by the developer (the Torahly backend, at{' '}
            <em>api.torahly.app</em>) only for the features below. Your account is shared with
            Torahly.
          </p>

          <h3>Account &amp; membership (optional)</h3>
          <p>
            You can use most of Covellah without an account. If you create one, to sync, join
            community features, or buy membership, we store your{' '}
            <strong>email address, a hashed password, and a display name</strong>, along with your
            membership status. If you subscribe, we also store the purchase details Google Play
            gives us to confirm your subscription, such as when it ends. You give us the rest. We
            don't get it from anywhere else.
          </p>

          <h3>Community features (optional)</h3>
          <p>
            If you post to the shared prayer board ("Sisters") or submit a recipe for review, what
            you post is stored on our server so the community can see it or an administrator can
            approve it. Don't post anything you consider private. Your private prayers in the app
            never leave your device.
          </p>

          <h3>Account backup (Keepers, optional)</h3>
          <p>
            Keepers can also save a backup to our server. It is encrypted on your device with your
            backup PIN before it is uploaded, so the server stores only ciphertext it can't read,
            and the developer can't recover it. We keep your two most recent backups. If you
            forget your PIN, they can't be restored.
          </p>

          <h3>Feast calendar</h3>
          <p>
            The app downloads a published biblical calendar so feast dates show up on their own.
            The download doesn't send any personal data.
          </p>

          <h3>Encrypted sync &amp; Spouse Sync (optional, off by default)</h3>
          <p>
            If you turn on encrypted sync, your data is{' '}
            <strong>end-to-end encrypted on your device</strong>, using a key made from your
            passphrase, before it is uploaded. The server stores only ciphertext it can't read, and
            the developer can't recover it. If you forget your passphrase, that data can't be
            recovered. This is intentional.
          </p>
          <p>
            Spouse Sync is optional and off by default. If you turn it on and pair with your
            spouse, your device works out a small, coarse signal, such as your current cycle phase,
            and encrypts it with a key shared only between the two paired devices. The server
            stores only the encrypted token. It never receives dates, flow, symptoms, weights,
            calories, journal entries, or mood. You can unlink at any time, and unlinking takes
            effect immediately and silently.
          </p>

          <h2 id="permissions">3. Device permissions and how they are used</h2>
          <dl className="doc-perms">
            <div>
              <dt>Camera</dt>
              <dd>
                Used only when you open the supplement and food scanner, to read a label or
                barcode. Images are processed on your device. Phones without a camera can still
                install the app.
              </dd>
            </div>
            <div>
              <dt>Microphone (record audio)</dt>
              <dd>
                Used only for hands-free voice features, such as counting workout reps by voice.
                Covellah does not record speech or upload it for storage. Your device's own speech
                service may do the recognition.
              </dd>
            </div>
            <div>
              <dt>Physical activity &amp; step counter</dt>
              <dd>
                Used to count your steps for Health. A foreground service keeps counting while the
                app is closed, and the boot permission lets it resume after your phone restarts.
                Step data stays on your device.
              </dd>
            </div>
            <div>
              <dt>Notifications</dt>
              <dd>
                Used for the reminders you set: prayer, fasting and eating window, and supplements.
              </dd>
            </div>
            <div>
              <dt>Internet</dt>
              <dd>
                Used for the server features in section 2 and for food and nutrition lookups.
                Covellah requests no location permission.
              </dd>
            </div>
          </dl>

          <h2 id="third-parties">4. Third-party services</h2>
          <p>Third parties are limited to what specific features need:</p>
          <ul>
            <li>
              <strong>Torahly backend</strong> (run by the developer): accounts, community
              features, calendar publishing, and optional encrypted sync and account backup, as
              described above.
            </li>
            <li>
              <strong>Open Food Facts</strong>: when you scan or look up a food, the app queries
              this public database for product and nutrition information.
            </li>
            <li>
              <strong>Google Play</strong>: distributes the app and processes the Keepers
              subscription ($29.99 a year or $3.99 a month). Google handles payment and may handle
              diagnostic information under its own privacy policy. Covellah never sees or stores
              your card details.
            </li>
          </ul>
          <p>
            Covellah includes <strong>no</strong> third-party advertising networks or analytics and
            tracking SDKs, and doesn't sell or share your personal information for advertising.
          </p>

          <h2 id="use">5. How we use your information</h2>
          <p>
            We use information only to run the app's features: your account and membership,
            showing and moderating what you post, syncing your data when you turn it on, delivering
            your reminders, and keeping the service secure. We don't use your personal tracking
            data to profile you or to advertise.
          </p>

          <h2 id="retention">6. Data retention</h2>
          <p>
            On-device data stays until you delete it in the app or uninstall Covellah. Account and
            community data on our server is kept while your account is active, until you ask us to
            delete it. Encrypted sync data and account backups are kept as ciphertext until you
            delete them or close your account. Backup files in your own folder stay until you
            delete them.
          </p>

          <h2 id="choices">7. Your choices and rights</h2>
          <ul>
            <li>
              <strong>Access &amp; export</strong>: you can export your data from within the app.
            </li>
            <li>
              <strong>Delete data</strong>: you can delete individual entries or clear data on your
              device at any time.
            </li>
            <li>
              <strong>Delete your account</strong>: to delete your account and its server-side data
              (including community posts, account backups, and any encrypted sync data), email us at the address
              below and we will process your request.
            </li>
            <li>
              <strong>Withdraw permissions</strong>: you can revoke camera, microphone, activity,
              or notification permissions in your device settings. Features that need them stop
              working.
            </li>
          </ul>

          <h2 id="security">8. Security</h2>
          <p>
            We protect your information with hashed passwords and, for backups and encrypted
            sync, encryption on your device before anything is saved or uploaded. No method of storage or transmission is
            perfectly secure, so Covellah keeps the most sensitive data on your device by default.
          </p>

          <h2 id="children">9. Children</h2>
          <p>
            Covellah is for adult women and isn't directed to children under 13, or under the
            minimum age of digital consent in your country. We don't knowingly collect personal
            information from children. If you think a child has given us information, contact us
            and we will delete it.
          </p>

          <h2 id="changes">10. Changes to this policy</h2>
          <p>
            We may update this policy as the app changes. When we do, we will change the "Last
            updated" date above and, where appropriate, tell you in the app or on our website. If
            you keep using Covellah after an update, you accept the revised policy.
          </p>

          <h2 id="contact">11. Contact us</h2>
          <p>
            For questions about this policy or to use your rights, contact the developer at:
          </p>
          <p>
            <a href="mailto:dahlialove@protonmail.com">dahlialove@protonmail.com</a>
          </p>
        </div>
      </article>

      <aside className="col-side">
        <nav className="card toc" aria-label="On this page">
          <h3 className="card-title">On this page</h3>
          <ol>
            {SECTIONS.map(([id, title], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  {index + 1}. {title}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </aside>
    </main>
  )
}
